import { useMemo, useRef, useState } from 'react'
import { Empty, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import type { AccountStatsRow, AccountsStatistics } from '../services/api'

const { Text } = Typography

/* Графики нарисованы вручную на SVG - без сторонней библиотеки.
   Правила простые и одни на все три графика:
   - величину показывает один цвет, разные цвета не для красоты;
   - столбик тонкий, со скруглённым концом и прямым основанием;
   - сетка - волосяная линия, она не спорит с данными;
   - подписи всегда текстовым цветом, никогда цветом серии;
   - у каждой отметки своя подсказка при наведении. */

const BAR_THICKNESS = 18
const BAR_GAP = 12
const RADIUS = 4

type Tip = { x: number; y: number; lines: string[]; title: string } | null

// Столбик с круглым концом и прямым основанием - как требует спецификация меток.
function horizontalBarPath(x: number, y: number, width: number, height: number): string {
    const r = Math.min(RADIUS, width)
    if (width <= r) return `M${x},${y}h${width}v${height}h${-width}z`
    return `M${x},${y}h${width - r}a${r},${r} 0 0 1 ${r},${r}v${height - 2 * r}a${r},${r} 0 0 1 ${-r},${r}h${-(width - r)}z`
}

function niceTicks(max: number, count = 4): number[] {
    if (max <= 0) return [0]
    const rough = max / count
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
    const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((value) => value >= rough) || magnitude * 10
    const ticks: number[] = []
    for (let value = 0; value <= max + step / 2; value += step) ticks.push(Math.round(value))
    return ticks
}

function Tooltip({ tip }: { tip: Tip }) {
    if (!tip) return null
    return (
        <div className="viz-tooltip" style={{ left: tip.x + 14, top: tip.y - 10 }}>
            <b>{tip.title}</b>
            {tip.lines.map((line) => <div key={line}>{line}</div>)}
        </div>
    )
}

/** Динамика: сколько сообщений уходило по дням и в какие дни ловили блокировки. */
function TimelineChart({ timeline }: { timeline: AccountsStatistics['timeline'] }) {
    const box = useRef<HTMLDivElement>(null)
    const [tip, setTip] = useState<Tip>(null)
    const [hover, setHover] = useState<number | null>(null)

    const W = 920
    const H = 260
    const padding = { top: 18, right: 24, bottom: 34, left: 48 }
    const innerW = W - padding.left - padding.right
    const innerH = H - padding.top - padding.bottom

    const maxSent = Math.max(1, ...timeline.map((point) => point.sent))
    const ticks = niceTicks(maxSent)
    const top = Math.max(...ticks, maxSent)

    const pointX = (index: number) => padding.left + (timeline.length <= 1 ? innerW / 2 : (innerW * index) / (timeline.length - 1))
    const pointY = (value: number) => padding.top + innerH - (innerH * value) / top

    const line = timeline.map((point, index) => `${index === 0 ? 'M' : 'L'}${pointX(index).toFixed(1)},${pointY(point.sent).toFixed(1)}`).join('')
    const area = `${line}L${pointX(timeline.length - 1).toFixed(1)},${padding.top + innerH}L${pointX(0).toFixed(1)},${padding.top + innerH}z`

    // Курсор ищет ближайший день: целиться в линию толщиной 2px никто не должен.
    const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        const scale = W / rect.width
        const svgX = (event.clientX - rect.left) * scale
        const ratio = (svgX - padding.left) / innerW
        const index = Math.round(ratio * (timeline.length - 1))
        if (index < 0 || index >= timeline.length) { setHover(null); setTip(null); return }
        const point = timeline[index]
        const hostRect = box.current?.getBoundingClientRect()
        setHover(index)
        setTip({
            x: hostRect ? event.clientX - hostRect.left : 0,
            y: hostRect ? event.clientY - hostRect.top : 0,
            title: dayjs(point.date).format('D MMMM YYYY'),
            lines: [
                `Отправлено: ${point.sent}`,
                `Лидов: ${point.leads}`,
                ...(point.blocks ? [`Блокировок: ${point.blocks}`] : []),
            ],
        })
    }

    const blocked = timeline.map((point, index) => ({ point, index })).filter((item) => item.point.blocks > 0)

    return (
        <div className="viz-card" style={{ position: 'relative' }} ref={box}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }} align="start">
                <div>
                    <Text strong>Отправка по дням</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>Сколько сообщений уходило и когда ловили блокировки</Text></div>
                </div>
                {/* Легенда: две отметки - значит опознание не должно держаться на цвете. */}
                <Space size={14}>
                    <Space size={6}>
                        <svg width="18" height="8" aria-hidden><line x1="1" y1="4" x2="17" y2="4" stroke="var(--viz-series)" strokeWidth="2" strokeLinecap="round" /></svg>
                        <Text style={{ fontSize: 12 }}>Сообщения</Text>
                    </Space>
                    <Space size={6}>
                        <svg width="10" height="10" aria-hidden><circle cx="5" cy="5" r="4" fill="var(--viz-critical)" /></svg>
                        <Text style={{ fontSize: 12 }}>Блокировка</Text>
                    </Space>
                </Space>
            </Space>

            <svg
                viewBox={`0 0 ${W} ${H}`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
                onPointerMove={onMove}
                onPointerLeave={() => { setHover(null); setTip(null) }}
                role="img"
                aria-label="График отправки сообщений по дням"
            >
                {ticks.map((tick) => (
                    <g key={tick}>
                        <line
                            x1={padding.left} x2={W - padding.right}
                            y1={pointY(tick)} y2={pointY(tick)}
                            stroke="var(--viz-grid)" strokeWidth="1"
                        />
                        <text x={padding.left - 8} y={pointY(tick) + 4} textAnchor="end" fontSize="11" fill="var(--viz-ink-muted)">{tick}</text>
                    </g>
                ))}

                <path d={area} fill="var(--viz-series-wash)" />
                <path d={line} fill="none" stroke="var(--viz-series)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

                {/* Дни с блокировками: отметка с кольцом цвета поверхности, чтобы
                    не сливалась с линией, плюс подпись внизу. */}
                {blocked.map(({ point, index }) => (
                    <circle
                        key={point.date}
                        cx={pointX(index)} cy={pointY(point.sent)} r={5}
                        fill="var(--viz-critical)" stroke="var(--viz-surface)" strokeWidth="2"
                    />
                ))}

                {hover !== null && (
                    <g>
                        <line
                            x1={pointX(hover)} x2={pointX(hover)}
                            y1={padding.top} y2={padding.top + innerH}
                            stroke="var(--viz-axis)" strokeWidth="1"
                        />
                        <circle
                            cx={pointX(hover)} cy={pointY(timeline[hover].sent)} r={5}
                            fill="var(--viz-series)" stroke="var(--viz-surface)" strokeWidth="2"
                        />
                    </g>
                )}

                <line
                    x1={padding.left} x2={W - padding.right}
                    y1={padding.top + innerH} y2={padding.top + innerH}
                    stroke="var(--viz-axis)" strokeWidth="1"
                />

                {timeline.map((point, index) => {
                    // Подписываем не каждый день, иначе даты налезут друг на друга.
                    const step = Math.max(1, Math.ceil(timeline.length / 8))
                    if (index % step !== 0 && index !== timeline.length - 1) return null
                    return (
                        <text
                            key={point.date}
                            x={pointX(index)} y={H - 12}
                            textAnchor="middle" fontSize="11" fill="var(--viz-ink-muted)"
                        >
                            {dayjs(point.date).format('DD.MM')}
                        </text>
                    )
                })}
            </svg>
            <Tooltip tip={tip} />
        </div>
    )
}

/** Сравнение величин: строка на аккаунт, длина - выработка. */
function AccountsBarChart({ rows, days }: { rows: AccountStatsRow[]; days: number }) {
    const box = useRef<HTMLDivElement>(null)
    const [tip, setTip] = useState<Tip>(null)

    const data = useMemo(
        () => [...rows].sort((a, b) => b.sent_period - a.sent_period).slice(0, 12),
        [rows]
    )
    const W = 920
    const padding = { top: 10, right: 70, bottom: 26, left: 186 }
    const H = padding.top + padding.bottom + data.length * (BAR_THICKNESS + BAR_GAP)
    const innerW = W - padding.left - padding.right
    const max = Math.max(1, ...data.map((row) => row.sent_period))
    const ticks = niceTicks(max)
    const top = Math.max(...ticks, max)

    const label = (row: AccountStatsRow) => row.name || (row.username ? `@${row.username}` : row.phone)

    if (!data.length) return <div className="viz-card"><Empty description="Нет аккаунтов под отбор" /></div>

    return (
        <div className="viz-card" style={{ position: 'relative' }} ref={box}>
            <Text strong>Выработка за {days} дн.</Text>
            <div style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Сколько сообщений отправил каждый аккаунт</Text>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Выработка аккаунтов">
                {ticks.map((tick) => (
                    <line
                        key={tick}
                        x1={padding.left + (innerW * tick) / top} x2={padding.left + (innerW * tick) / top}
                        y1={padding.top} y2={H - padding.bottom}
                        stroke="var(--viz-grid)" strokeWidth="1"
                    />
                ))}

                {data.map((row, index) => {
                    const y = padding.top + index * (BAR_THICKNESS + BAR_GAP)
                    const width = Math.max(2, (innerW * row.sent_period) / top)
                    return (
                        <g
                            key={row.id}
                            onPointerMove={(event) => {
                                const hostRect = box.current?.getBoundingClientRect()
                                setTip({
                                    x: hostRect ? event.clientX - hostRect.left : 0,
                                    y: hostRect ? event.clientY - hostRect.top : 0,
                                    title: label(row),
                                    lines: [
                                        `За период: ${row.sent_period} сообщ. · ${row.leads_period} лидов`,
                                        `За всё время: ${row.sent_total} сообщ. · ${row.leads_total} лидов`,
                                        `Блокировок: ${row.blocks_count}`,
                                    ],
                                })
                            }}
                            onPointerLeave={() => setTip(null)}
                        >
                            {/* Прозрачная зона наведения шире самого столбика. */}
                            <rect x={padding.left} y={y - BAR_GAP / 2} width={innerW} height={BAR_THICKNESS + BAR_GAP} fill="transparent" />
                            <text x={padding.left - 10} y={y + BAR_THICKNESS - 4} textAnchor="end" fontSize="12" fill="var(--viz-ink-secondary)">
                                {label(row).length > 24 ? `${label(row).slice(0, 23)}…` : label(row)}
                            </text>
                            <path d={horizontalBarPath(padding.left, y, width, BAR_THICKNESS)} fill="var(--viz-series)" />
                            <text x={padding.left + width + 8} y={y + BAR_THICKNESS - 4} fontSize="12" fill="var(--viz-ink)">
                                {row.sent_period}
                            </text>
                            {Boolean(row.blocks_count) && (
                                <text x={W - 8} y={y + BAR_THICKNESS - 4} textAnchor="end" fontSize="11" fill="var(--viz-ink-muted)">
                                    {row.blocks_count} бл.
                                </text>
                            )}
                        </g>
                    )
                })}

                <line x1={padding.left} x2={padding.left} y1={padding.top} y2={H - padding.bottom} stroke="var(--viz-axis)" strokeWidth="1" />
                {ticks.map((tick) => (
                    <text
                        key={tick}
                        x={padding.left + (innerW * tick) / top} y={H - 8}
                        textAnchor="middle" fontSize="11" fill="var(--viz-ink-muted)"
                    >
                        {tick}
                    </text>
                ))}
            </svg>
            <Tooltip tip={tip} />
        </div>
    )
}

/** Из-за чего блокируются: сравнение количества случаев по типам. */
function BlockTypesChart({ items, labelOf }: { items: AccountsStatistics['block_types']; labelOf: (code: string) => string }) {
    const box = useRef<HTMLDivElement>(null)
    const [tip, setTip] = useState<Tip>(null)

    if (!items.length) {
        return (
            <div className="viz-card">
                <Text strong>Из-за чего блокируются</Text>
                <div style={{ marginTop: 12 }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Блокировок пока не было" />
                </div>
            </div>
        )
    }

    const W = 920
    const padding = { top: 10, right: 70, bottom: 26, left: 210 }
    const H = padding.top + padding.bottom + items.length * (BAR_THICKNESS + BAR_GAP)
    const innerW = W - padding.left - padding.right
    const max = Math.max(1, ...items.map((item) => item.count))
    const ticks = niceTicks(max)
    const top = Math.max(...ticks, max)

    return (
        <div className="viz-card" style={{ position: 'relative' }} ref={box}>
            <Text strong>Из-за чего блокируются</Text>
            <div style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Сколько раз случался каждый вид блокировки</Text>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Виды блокировок">
                {ticks.map((tick) => (
                    <line
                        key={tick}
                        x1={padding.left + (innerW * tick) / top} x2={padding.left + (innerW * tick) / top}
                        y1={padding.top} y2={H - padding.bottom}
                        stroke="var(--viz-grid)" strokeWidth="1"
                    />
                ))}
                {items.map((item, index) => {
                    const y = padding.top + index * (BAR_THICKNESS + BAR_GAP)
                    const width = Math.max(2, (innerW * item.count) / top)
                    const name = labelOf(item.block_type)
                    return (
                        <g
                            key={item.block_type}
                            onPointerMove={(event) => {
                                const hostRect = box.current?.getBoundingClientRect()
                                setTip({
                                    x: hostRect ? event.clientX - hostRect.left : 0,
                                    y: hostRect ? event.clientY - hostRect.top : 0,
                                    title: name,
                                    lines: [
                                        `Случаев: ${item.count}`,
                                        `В среднем до неё: ${item.avg_sent_before ?? '—'} сообщ.`,
                                        `Последний раз: ${item.last_at ? dayjs(item.last_at).format('DD.MM.YYYY HH:mm') : '—'}`,
                                    ],
                                })
                            }}
                            onPointerLeave={() => setTip(null)}
                        >
                            <rect x={padding.left} y={y - BAR_GAP / 2} width={innerW} height={BAR_THICKNESS + BAR_GAP} fill="transparent" />
                            <text x={padding.left - 10} y={y + BAR_THICKNESS - 4} textAnchor="end" fontSize="12" fill="var(--viz-ink-secondary)">
                                {name.length > 28 ? `${name.slice(0, 27)}…` : name}
                            </text>
                            <path d={horizontalBarPath(padding.left, y, width, BAR_THICKNESS)} fill="var(--viz-critical)" />
                            <text x={padding.left + width + 8} y={y + BAR_THICKNESS - 4} fontSize="12" fill="var(--viz-ink)">
                                {item.count}
                            </text>
                        </g>
                    )
                })}
                <line x1={padding.left} x2={padding.left} y1={padding.top} y2={H - padding.bottom} stroke="var(--viz-axis)" strokeWidth="1" />
                {ticks.map((tick) => (
                    <text key={tick} x={padding.left + (innerW * tick) / top} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--viz-ink-muted)">
                        {tick}
                    </text>
                ))}
            </svg>
            <Tooltip tip={tip} />
        </div>
    )
}

export default function AccountsCharts({
    data,
    labelOf,
}: {
    data: AccountsStatistics
    labelOf: (code: string) => string
}) {
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }} className="viz-root">
            {data.timeline?.length ? <TimelineChart timeline={data.timeline} /> : null}
            <AccountsBarChart rows={data.accounts} days={data.days} />
            <BlockTypesChart items={data.block_types} labelOf={labelOf} />
        </Space>
    )
}
